import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBedge } from "@/layouts/page-container/shared/status-bedge";
import { copyToClipboard } from "@/utils/copy-to-clipboard";
import { formatCurrency } from "@/utils/format/currency";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowTopRightOnSquareIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Fragment, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { generatePrivateRoomUrl } from "../../utils/generate-private-room-url";
import { UpdatedPrivateOfficeBadge } from "../../utils/updated-private-office-badge";
import { PrivateRoomAdminDetailProps } from "./private-room-admin-details.types";
import {
  LIFECYCLE_STATE_LABELS,
  PrivateOfficeLifecycleState,
} from "@/@types/private-office.types";
import { BedgeOutline } from "./shared/bedge-outline";
import { CopyButton } from "./shared/copy-button";
import {
  AmenityEntity,
  PRIVATE_OFFICE_SUB_TYPES,
} from "../../adapters/get-privateroom/get-privateroom.types";
import AmenityDetails from "./shared/amenities-details";
import Icon from "@/components/ui/icon";
import { icons } from "lucide-react";
import { handleSelectIconCategory } from "../../utils/get-icons";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { ListImageWithFallback } from "@/features/vision-360/components/ListImageWithFallback";
import { convertPOCategoryToLabel } from "@/utils/convert-po-category-to-label";
import { usePrivateOfficeEditRoute } from "@/constants/routes";
import { ModalDialog } from "@/components/data-display/modal-dialog/modal-dialog";
import { privateOfficeService } from "@/services/private-office-service";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useToast } from "@/components/ui/use-toast";
import { parseISO } from "date-fns";

export const PrivateRoomAdminDetails = ({
  privateRoom,
  spaceSelected,
  isOpen,
  onClose,
  isLoading = false,
  daysSinceUpdate,
}: PrivateRoomAdminDetailProps) => {
  const { pathname } = useLocation();
  const [showTouchModal, setShowTouchModal] = useState(false);
  const [touchLoading, setTouchLoading] = useState(false);
  const editRoute = usePrivateOfficeEditRoute(privateRoom.id.toString());
  const [localUpdatedAt, setLocalUpdatedAt] = useState<string>(
    privateRoom.updated_at,
  );
  const [localDays, setLocalDays] = useState<number | null | undefined>(
    daysSinceUpdate,
  );
  const { toast } = useToast();

  const privateRoomUpdatedAt = privateRoom.updated_at;
  const privateRoomUpdatedAtMs = parseISO(privateRoomUpdatedAt).getTime();

  useEffect(() => {
    setLocalUpdatedAt(privateRoomUpdatedAt);
    setLocalDays(daysSinceUpdate);
    // Intentionally omit `privateRoom.updated_at` object identity: compare by instant (ms) to avoid
    // effect churn when the parent passes a new Date with the same timestamp.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: id, instant, loader days
  }, [privateRoom.id, privateRoomUpdatedAtMs, daysSinceUpdate]);

  const detailsPrivateRoom = () => {
    if (privateRoom) {
      return {
        ...privateRoom,
        end_contracted_at: null,
        pictures: [
          privateRoom.picture_url,
          privateRoom.picture_url1,
          privateRoom.picture_url2,
          privateRoom.picture_url3,
          privateRoom.picture_url4,
          privateRoom.picture_url5,
        ].filter((url) => !!url),
        space: {
          ...spaceSelected,
          adm_mail: "",
        },
        amenities: Array.isArray(privateRoom.amenities)
          ? privateRoom.amenities
          : privateRoom.amenities?.split(",") || null,
        extra_services: Array.isArray(privateRoom.extra_services)
          ? privateRoom.extra_services
          : privateRoom.extra_services?.split(",") || null,
      };
    }
  };

  const showIconAmenity = (
    amenity: AmenityEntity,
  ): keyof typeof icons | null => {
    if (amenity.is_automatic_generated) return "Sparkles";
    if (amenity.is_internal_amenity) return "CircleDollarSign";
    if (amenity.attributes?.filter((e) => e.name !== "score").length > 0)
      return "Info";
    return null;
  };

  const OpenRoomDetails = privateRoom ? true : isOpen;
  const [hoveredAmenity, setHoveredAmenity] = useState<AmenityEntity | null>();

  const handleCloseModal = () => {
    onClose?.();
  };
  const arrToStringCopy = function (arrItems: string[] | undefined) {
    let stringToCopy: string = "";

    arrItems?.map((item) => {
      stringToCopy += `${item}; `;
    });

    return stringToCopy;
  };

  const handleTouchPrivateOffice = async () => {
    if (!privateRoom.id) return;

    try {
      setTouchLoading(true);
      await privateOfficeService.touchPrivateOffice(privateRoom.id, ["status"]);

      setLocalUpdatedAt(new Date().toISOString());
      setLocalDays(0);
      setShowTouchModal(false);
    } catch (error) {
      console.error("Erro ao atualizar data de atualização:", error);
      toast({
        title: "Erro ao atualizar data de atualização",
        variant: "destructive",
      });
    } finally {
      setTouchLoading(false);
    }
  };

  const isReadOnly =
    privateRoom?.origin === "integration" ||
    privateRoom?.sub_type === PRIVATE_OFFICE_SUB_TYPES.LEGACY;
  const getOriginAndSource = (): string => {
    if (privateRoom?.source === "vip-office") {
      return "Integração / VIP Office";
    }
    return privateRoom?.origin + " / " + privateRoom?.source;
  };

  return (
    <>
      <Transition.Root show={OpenRoomDetails} as={Fragment}>
        <Dialog
          id="private-room-details"
          as="div"
          className="relative z-50"
          onClose={handleCloseModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 ">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto relative w-screen max-w-[68rem]">
                    <Transition.Child
                      as={Fragment}
                      enter="ease-in-out duration-500"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="ease-in-out duration-500"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <div className="absolute right-0 top-0 flex pr-2 mr-2 pt-5 sm:-ml-10 sm:pr-4">
                        <button
                          type="button"
                          id="close-modal"
                          className="relative bg-white border border-zinc-200 hover:bg-zinc-100 p-2 h-[36px] w-[36px] rounded-md text-zinc-950 flex items-center justify-center"
                          onClick={handleCloseModal}
                        >
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </Transition.Child>
                    <div className="flex h-full flex-col overflow-y-scroll bg-white w-full pt-6 shadow-xl">
                      {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        </div>
                      ) : (
                        <>
                          <div className="px-4">
                            <Dialog.Title className="text-base leading-6 flex flex-col gap-4">
                              <span className="text-[28px] text-zinc-950 font-semibold flex items-center gap-2">
                                {detailsPrivateRoom()?.label}{" "}
                                <div className="bg-zinc-500 w-[3px] h-[3px] rounded-full" />{" "}
                                <span className="text-sm text-zinc-500 font-normal">
                                  {detailsPrivateRoom()?.search_id}
                                </span>
                              </span>

                              <span className="text-sm text-zinc-500 font-normal flex items-center gap-2">
                                {detailsPrivateRoom()?.space?.name}{" "}
                                <div className="bg-zinc-500 w-[3px] h-[3px] rounded-full" />{" "}
                                {detailsPrivateRoom()?.space?.id}
                              </span>
                            </Dialog.Title>
                          </div>

                          <div className="flex gap-3 p-4 border-b border-zinc-200 pb-4">
                            <div className="border rounded-md border-zinc-200 shadow-sm text-zinc-950 max-h-[36px] py-2 px-4 shadow-sm hover:bg-gray-100">
                              <div className="flex items-center justify-center space-x-2 h-full">
                                <ArrowTopRightOnSquareIcon
                                  className="text-zinc-950 hover:text-black"
                                  width={18}
                                />
                                <Link
                                  to={generatePrivateRoomUrl(
                                    `${detailsPrivateRoom()?.search_id}`,
                                    "80",
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium"
                                  onClick={() => {
                                    if (privateRoom.id) {
                                      sendTrackDataServer({
                                        url: pathname,
                                        eventName: "VIEW_PAGE_CLICKED",
                                        productType: "PO",
                                        data: {
                                          room_id: privateRoom.id,
                                        },
                                      });
                                    }
                                  }}
                                >
                                  Acessar página
                                </Link>
                              </div>
                            </div>

                            {privateRoom &&
                              (isReadOnly ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex">
                                        <div
                                          className="border rounded-md border-zinc-200 shadow-sm text-zinc-400 max-h-[36px] py-2 px-4 bg-zinc-50 pointer-events-none opacity-50 flex items-center justify-center space-x-2 h-full text-sm font-medium select-none"
                                          aria-disabled="true"
                                        >
                                          <PencilSquareIcon
                                            className="text-zinc-400 hover:text-zinc-400"
                                            width={18}
                                          />
                                          <span>Editar</span>
                                        </div>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="bottom"
                                      className="max-w-[280px] text-center !bg-zinc-900 text-white"
                                    >
                                      {privateRoom?.sub_type ===
                                      PRIVATE_OFFICE_SUB_TYPES.LEGACY
                                        ? "Esta sala é um registro legado. Ela não pode ser editada e nem removida pela área administrativa."
                                        : "Esta sala é originada de uma integração com um sistema parceiro. Ela não pode ser editada e nem removida pela área administrativa."}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <div className="border rounded-md border-zinc-200 shadow-sm text-zinc-950 max-h-[36px] py-2 px-4 shadow-sm hover:bg-gray-100">
                                  <div className="flex items-center justify-center space-x-2 h-full">
                                    <PencilSquareIcon
                                      className="text-zinc-950 hover:text-black"
                                      width={18}
                                    />
                                    <Link
                                      to={editRoute}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm font-medium"
                                      onClick={() => {
                                        if (privateRoom.id) {
                                          sendTrackDataServer({
                                            url: pathname,
                                            eventName: "EDIT_ROOM_CLICKED",
                                            productType: "PO",
                                            data: {
                                              room_id: privateRoom.id,
                                            },
                                          });
                                        }
                                      }}
                                    >
                                      Editar
                                    </Link>
                                  </div>
                                </div>
                              ))}
                          </div>

                          <div className="flex h-full">
                            <div className="flex flex-col justify-start w-100 max-w-2xl boder border-r">
                              <div className="flex items-center gap-3 pt-2.5 pb-0 sm:px-4 sm:pb-6">
                                <div className="flex-auto">
                                  <div className="flex gap-2 items-center text-sm">
                                    <h3 className="font-medium">Imagens</h3>
                                    <BedgeOutline
                                      text={
                                        detailsPrivateRoom()?.pictures
                                          ?.length as number
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="flex items-end text-right">
                                  <Link
                                    className="border rounded-md border-zinc-200 shadow-sm py-2 px-4 shadow-sm hover:bg-zinc-100 font-medium text-sm max-w-[125px] max-h-[36px] flex items-center justify-center"
                                    to={`https://woba.typeform.com/to/GPWRdb8i#space_id=${detailsPrivateRoom()?.space?.id}&privateroom_id=${detailsPrivateRoom()?.id}&city=${detailsPrivateRoom()?.space?.cityname}&bairro=${detailsPrivateRoom()?.space?.neighborhood}&space_name=${detailsPrivateRoom()?.space?.name}&email=${detailsPrivateRoom()?.space?.adm_mail}&room_name=${detailsPrivateRoom()?.label}`}
                                    target="_blank"
                                  >
                                    Reportar foto
                                  </Link>
                                </div>
                              </div>

                              <div className="w-full flex items-center justify-center">
                                {(detailsPrivateRoom()?.pictures?.length ?? 0) >
                                0 ? (
                                  <Carousel className="w-full min-h-[28rem] p-0 max-w-[80%]">
                                    <CarouselContent className="px-5">
                                      {detailsPrivateRoom()?.pictures?.map(
                                        (item, index) => (
                                          <CarouselItem key={index}>
                                            <div className="p-2">
                                              <Card className="p-0 ">
                                                <CardContent className="flex  items-center justify-center p-0 relative">
                                                  <Badge className="absolute left-2 top-2 bg-gray-200 text-black hover:bg-gray-200">
                                                    Foto sala
                                                  </Badge>
                                                  <ListImageWithFallback
                                                    src={item as string}
                                                    alt={`Imagem ${index + 1}`}
                                                    className="rounded-md min-h-[26rem] w-full object-cover object-center"
                                                  />
                                                </CardContent>
                                              </Card>
                                            </div>
                                          </CarouselItem>
                                        ),
                                      )}
                                    </CarouselContent>
                                    <CarouselPrevious />
                                    <CarouselNext />
                                  </Carousel>
                                ) : (
                                  <div className=" m-4 border bg-card text-card-foreground shadow-none rounded-md w-full">
                                    <div className="p-6 px-3 py-2 rounded-md flex items-center justify-center text-sm text-zinc-500">
                                      Não cadastrado
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-col p-4 sm:p-4 pb-4">
                                <div className="flex justify-between items-center gap-2 mb-4 text-sm">
                                  <h3 className="font-medium">Endereço</h3>
                                  <div className="flex items-center">
                                    <Link
                                      className="bg-white border border-zinc-200 rounded-md shadow-sm text-zinc-950 font-medium py-2 px-4 mr-2 flex items-center text-sm max-h-[36px] hover:bg-zinc-100"
                                      to={`https://maps.google.com/maps/place/${detailsPrivateRoom()?.space?.address}`}
                                      target="_blank"
                                    >
                                      <ArrowTopRightOnSquareIcon
                                        className="text-zinc-950 hover:text-black mr-2"
                                        width={18}
                                      />{" "}
                                      Google Maps
                                    </Link>

                                    <CopyButton
                                      action={() => {
                                        copyToClipboard(
                                          detailsPrivateRoom()?.space
                                            ?.address as string,
                                        );
                                        if (privateRoom.id) {
                                          sendTrackDataServer({
                                            url: pathname,
                                            eventName: "ADDRESS_COPIED",
                                            productType: "PO",
                                            data: {
                                              room_id: privateRoom.id,
                                            },
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                                <Card className="shadow-none rounded-md">
                                  <CardContent className="px-3 py-2 rounded-md">
                                    <CardDescription className="text-zinc-500">
                                      {detailsPrivateRoom()?.space?.address}
                                    </CardDescription>
                                  </CardContent>
                                </Card>
                              </div>
                              {detailsPrivateRoom()?.advantages && (
                                <div className="flex flex-col p-4 sm:p-4 pb-4">
                                  <div className="flex justify-between items-center gap-2 mb-4 text-sm">
                                    <h3 className="font-medium">Vantagens </h3>
                                    <CopyButton
                                      action={() =>
                                        copyToClipboard(
                                          detailsPrivateRoom()
                                            ?.advantages as string,
                                        )
                                      }
                                    />
                                  </div>
                                  <Card className="shadow-none rounded-md">
                                    <CardContent className="px-3 py-2 rounded-md">
                                      <CardDescription className="text-zinc-500">
                                        {detailsPrivateRoom()?.advantages}
                                      </CardDescription>
                                    </CardContent>
                                  </Card>
                                </div>
                              )}

                              {detailsPrivateRoom()?.notes && (
                                <div className="flex flex-col p-4 sm:p-4 sm:pb-0 pb-0">
                                  <div className="flex justify-between items-center gap-2 mb-4 text-sm">
                                    <h3 className="font-medium">Notas</h3>
                                    <CopyButton
                                      action={() => {
                                        copyToClipboard(
                                          detailsPrivateRoom()?.notes as string,
                                        );
                                        if (privateRoom.id) {
                                          sendTrackDataServer({
                                            url: pathname,
                                            eventName: "NOTES_COPIED",
                                            productType: "PO",
                                            data: {
                                              room_id: privateRoom.id,
                                            },
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  <Card className="shadow-none rounded-md">
                                    <CardContent className="px-3 py-2 rounded-md">
                                      <CardDescription className="text-zinc-500">
                                        {detailsPrivateRoom()?.notes}
                                      </CardDescription>
                                    </CardContent>
                                  </Card>
                                </div>
                              )}
                            </div>

                            <div className="flex-1 px-4 pt-2.5">
                              <Tabs
                                defaultValue="room-details"
                                className="w-full"
                              >
                                <TabsList className="w-full flex gap-2 bg-zinc-100 h-auto min-h-[36px]">
                                  <TabsTrigger
                                    value="room-details"
                                    className="flex-1 shadow-none text-sm border border-zinc-200 font-medium"
                                  >
                                    Sala
                                  </TabsTrigger>
                                </TabsList>
                                <TabsContent value="room-details">
                                  <div className="flex flex-col gap-2 pt-4 pb-4 sm:pb-6 sm:pt-4">
                                    <div className="flex gap-2">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        ID da Sala
                                      </span>{" "}
                                      <span className="text-zinc-500 text-sm">
                                        {detailsPrivateRoom()?.search_id}
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        Capacidade
                                      </span>{" "}
                                      <span className="text-zinc-500 text-sm">
                                        {detailsPrivateRoom()?.capacity}
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        Tamanho
                                      </span>{" "}
                                      <span className="text-zinc-500 text-sm">
                                        {detailsPrivateRoom()?.size
                                          ? `${detailsPrivateRoom()?.size} m2`
                                          : "-"}
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        Preço Anual
                                      </span>{" "}
                                      <span className="text-zinc-500 text-sm">
                                        {detailsPrivateRoom()?.yearly_price ||
                                          0}
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        Créditos
                                      </span>{" "}
                                      <span className="text-zinc-500 text-sm">
                                        {detailsPrivateRoom()?.credits || 0}
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        Preço Mensal
                                      </span>{" "}
                                      <span className="text-zinc-500 text-sm">
                                        {detailsPrivateRoom()?.monthly_price ||
                                          0}
                                      </span>
                                    </div>
                                    {privateRoom.id &&
                                      privateRoom?.origin === "integration" && (
                                        <div className="flex gap-2">
                                          <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                            Origem / Fonte
                                          </span>{" "}
                                          <span className="text-zinc-500 text-sm">
                                            {getOriginAndSource()}
                                          </span>
                                        </div>
                                      )}
                                    <div className="flex gap-2">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        {" "}
                                        Status
                                      </span>{" "}
                                      <StatusBedge
                                        status={
                                          detailsPrivateRoom()?.status || 0
                                        }
                                        type="STATUS_PRIVATE_OFFICE"
                                      />
                                    </div>
                                    {detailsPrivateRoom()?.lifecycle_state && (
                                      <div className="flex gap-2">
                                        <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                          Motivo
                                        </span>
                                        <span className="text-zinc-500 text-sm">
                                          {LIFECYCLE_STATE_LABELS[
                                            detailsPrivateRoom()
                                              ?.lifecycle_state as PrivateOfficeLifecycleState
                                          ] ??
                                            detailsPrivateRoom()
                                              ?.lifecycle_state}
                                        </span>
                                      </div>
                                    )}
                                    {detailsPrivateRoom()
                                      ?.availability_date && (
                                      <div className="flex gap-2">
                                        <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                          Previsão de disponibilidade
                                        </span>
                                        <span className="text-zinc-500 text-sm">
                                          {parseISO(
                                            detailsPrivateRoom()!.availability_date!.slice(
                                              0,
                                              10,
                                            ),
                                          ).toLocaleDateString("pt-BR")}
                                        </span>
                                      </div>
                                    )}
                                    <div className="flex gap-2 items-center">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        Atualizado há
                                      </span>{" "}
                                      <UpdatedPrivateOfficeBadge
                                        days={localDays}
                                        date={localUpdatedAt}
                                        dateAcknowledge={undefined}
                                      />
                                      {privateRoom.id && !isReadOnly && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setShowTouchModal(true)
                                          }
                                          className="ml-2 border rounded-md border-zinc-200 shadow-sm text-zinc-950 max-h-[36px] py-2 px-4 hover:bg-gray-100 flex items-center gap-2"
                                          title="Atualizar data de atualização"
                                        >
                                          <ArrowPathIcon className="h-4 w-4" />
                                          <span className="text-xs font-medium">
                                            Confirmar e atualizar
                                          </span>
                                        </button>
                                      )}
                                    </div>

                                    <div className="flex gap-2">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        Taxa de Garantia
                                      </span>{" "}
                                      <span className="text-zinc-500 text-sm">
                                        {formatCurrency(
                                          Number(
                                            detailsPrivateRoom()?.guarantee_fee,
                                          ) || 0,
                                        )}
                                      </span>
                                    </div>

                                    <div className="flex gap-2">
                                      <span className="flex-none w-32 text-right font-medium text-sm text-zinc-950">
                                        Categoria
                                      </span>{" "}
                                      <span className="text-zinc-500 text-sm">
                                        {convertPOCategoryToLabel(
                                          detailsPrivateRoom()?.category || 0,
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                  {detailsPrivateRoom()
                                    ?.grouped_amenities?.filter(
                                      (e) => e.name !== "notIncluded",
                                    )
                                    .map((item) => (
                                      <div
                                        className="flex flex-col"
                                        key={item.name}
                                      >
                                        <div className="flex p-1 mb-1 justify-between items-center">
                                          <h3 className="font-medium flex items-center justify-between text-sm gap-3">
                                            <Icon
                                              name={
                                                handleSelectIconCategory(
                                                  item.name,
                                                ).icon
                                              }
                                            />
                                            {
                                              handleSelectIconCategory(
                                                item.name,
                                              ).name
                                            }{" "}
                                            <BedgeOutline
                                              text={
                                                item.amenities?.length as number
                                              }
                                            />
                                          </h3>

                                          <CopyButton
                                            action={() => {
                                              copyToClipboard(
                                                arrToStringCopy(
                                                  item.amenities.map(
                                                    (e) => e.name,
                                                  ),
                                                ),
                                              );
                                              if (privateRoom.id) {
                                                sendTrackDataServer({
                                                  url: pathname,
                                                  eventName: "AMENITY_CLICKED",
                                                  productType: "PO",
                                                  data: {
                                                    room_id: privateRoom.id,
                                                    amenity_category: item.name,
                                                  },
                                                });
                                              }
                                            }}
                                          />
                                        </div>
                                        <Card className="shadow-none border-none p-0 mb-3">
                                          <CardContent className="inline-block p-0 m-0">
                                            <div className="flex flex-wrap cursor-pointer">
                                              {item.amenities?.map(
                                                (amenity, index) => (
                                                  <div
                                                    className="relative"
                                                    key={amenity.id}
                                                  >
                                                    {hoveredAmenity?.name ===
                                                      amenity.name && (
                                                      <div className="absolute">
                                                        <AmenityDetails
                                                          amenity={amenity}
                                                        />
                                                      </div>
                                                    )}
                                                    <Badge
                                                      key={index}
                                                      className="mr-2 my-1 flex items-center"
                                                      onMouseEnter={() => {
                                                        if (
                                                          amenity.is_automatic_generated ||
                                                          amenity.is_internal_amenity ||
                                                          amenity.attributes?.filter(
                                                            (e) =>
                                                              e.name !==
                                                              "score",
                                                          ).length > 0
                                                        ) {
                                                          setHoveredAmenity(
                                                            amenity,
                                                          );
                                                        }
                                                      }}
                                                      onMouseLeave={() =>
                                                        setHoveredAmenity(null)
                                                      }
                                                    >
                                                      {showIconAmenity(
                                                        amenity,
                                                      ) ? (
                                                        <Icon
                                                          name={
                                                            showIconAmenity(
                                                              amenity,
                                                            ) || "Info"
                                                          }
                                                          style={{
                                                            marginRight: 4,
                                                          }}
                                                          size={16}
                                                        />
                                                      ) : null}
                                                      {amenity.name}
                                                    </Badge>
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          </CardContent>
                                        </Card>
                                      </div>
                                    ))}
                                </TabsContent>
                              </Tabs>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <ModalDialog
        open={showTouchModal}
        handleClose={() => setShowTouchModal(false)}
        handleConfirm={handleTouchPrivateOffice}
        title="Atualizar data de atualização"
        description="Tem certeza que deseja atualizar a data de atualização desta sala privativa?"
        closeLabel="Cancelar"
        submitLabel="Confirmar"
        showFooter={true}
        type="button"
        enableButton={!touchLoading}
      />
    </>
  );
};
